import React, { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { IPosition } from '@models/winnerSelection/ITablePositions';
import { PosInfo } from '../utils/posInfoMerge';
import { PositionListItem } from './PositionListItem';

/** Initial estimate высоты карточки для virtualizer'а.
 *  Реальная высота приходит из measureElement (ResizeObserver) — карточки
 *  без winner-имени ~64px, с winner ~84px. Берём промежуточное значение,
 *  чтобы стартовая пристрелка была близка к реальной средней.
 *  Без export — снаружи не используется (react-refresh/only-export-components). */
const POS_ITEM_HEIGHT = 72;

interface IProps {
    items: IPosition[];
    scrollRef: React.RefObject<HTMLElement | null>;
    currentKey: string | undefined;
    onSelect: (p: IPosition) => void;
    posInfo: Record<string, PosInfo>;
}

/**
 * Виртуализированный список позиций (1500+ карточек). Каждая карточка —
 * фиксированная высота POS_ITEM_HEIGHT (резерв под winner-строку, которая
 * может появиться после chunk-прогрева).
 *
 * getItemKey — стабильный ключ по position.key. Без него virtualizer сбрасывает
 * measurement-кеш при любом изменении массива items («скролл прыгает в начало»).
 *
 * scrollToIndex срабатывает ТОЛЬКО при реальной смене currentKey, не на каждое
 * обновление items (которые меняются при подгрузке offers-info chunk'ами).
 *
 * Сам рендер карточки вынесен в PositionListItem с React.memo — при смене
 * currentKey ре-рендерится только пара бывшая/новая активная, остальные
 * стабильны (см. PositionListItem.tsx).
 */
export const AsideList: React.FC<IProps> = ({ items, scrollRef, currentKey, onSelect, posInfo }) => {
    const rowVirtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => scrollRef.current,
        /* estimateSize — только initial guess; реальная высота приходит из
         *  measureElement (ResizeObserver), это нужно когда winner-имя
         *  переносится при узкой aside-панели и карточка растёт. */
        estimateSize: () => POS_ITEM_HEIGHT,
        overscan: 8,
        getItemKey: (index) => items[index]?.key ?? index,
    });

    const prevCurrentKeyRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (currentKey === prevCurrentKeyRef.current) return;
        prevCurrentKeyRef.current = currentKey;
        if (!currentKey) return;
        const idx = items.findIndex((p) => p.key === currentKey);
        if (idx >= 0) rowVirtualizer.scrollToIndex(idx, { align: 'auto' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentKey]);

    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();

    return (
        <div style={{ height: totalSize, position: 'relative', width: '100%' }}>
            {virtualRows.map((row) => {
                const p = items[row.index];
                return (
                    <PositionListItem
                        key={p.key}
                        position={p}
                        info={posInfo[p.key]}
                        isActive={p.key === currentKey}
                        top={row.start}
                        dataIndex={row.index}
                        onSelect={onSelect}
                        measureRef={rowVirtualizer.measureElement}
                    />
                );
            })}
        </div>
    );
};
